import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaLinkedinIn, FaFacebookF, FaInstagram, FaArrowRight, FaAward } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

interface MemberItem {
  id: number;
  name: string;
  email: string;
  company_name?: string;
  designation?: string;
  profile?: string;
  role?: string;
  sub_role?: string;
  about_company?: string;
  point?: number;
  delete?: number;
  status?: string;
  expire?: string;
}

const Teams = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [activeMembers, setActiveMembers] = useState<MemberItem[]>([]);

  useEffect(() => {
    const fetchActiveMembers = async () => {
      try {
        const response = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
        const list: MemberItem[] = Array.isArray(response.data) ? response.data : [];
        const currentYearStart = new Date('2024-01-01');
        const leadersOnly = list.filter((m) => {
          if (m.delete === 1) return false;
          if (m.role === 'super-admin' || m.role === 'visitor') return false;
          const str = `${m.designation || ''} ${m.sub_role || ''} ${m.role || ''}`.toLowerCase();

          return (
            str.includes('vice chairman') || str.includes('vice-chairman') || str.includes('vice chair') ||
            str.includes('chairman') ||
            str.includes('secretary') ||
            str.includes('treasurer') ||
            str.includes('president') ||
            str.includes('chapter leader')
          );
        });

        leadersOnly.sort((a, b) => {
          const getRank = (m: MemberItem) => {
            const str = `${m.designation || ''} ${m.sub_role || ''} ${m.role || ''}`.toLowerCase();
            if (str.includes('vice chairman') || str.includes('vice-chairman') || str.includes('vice chair')) return 2;
            if (str.includes('chairman') || str.includes('president') || str.includes('leader')) return 1;
            if (str.includes('secretary')) return 3;
            if (str.includes('treasurer')) return 4;
            return 5;
          };
          return getRank(a) - getRank(b);
        });

        const finalDisplay = leadersOnly.length > 0 ? leadersOnly : list.filter((m) => m.delete !== 1 && m.role !== 'super-admin');
        setActiveMembers(finalDisplay);
      } catch (error) {
        console.error('Error fetching active team members:', error);
      }
    };

    fetchActiveMembers();
  }, []);

  return (
    <section className={`py-20 px-4 sm:px-8 lg:px-[8%] ${isLight ? 'bg-slate-50 text-slate-900' : 'bg-[#060D1A] text-slate-100'} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className={`inline-flex items-center gap-1.5 ${isLight ? 'bg-amber-500/10 text-amber-700 border-amber-500/30' : 'bg-[#FDBC12]/10 text-[#FDBC12] border-[#FDBC12]/25'} text-[11px] font-black uppercase px-3.5 py-1 rounded-full border tracking-[0.12em] mb-3`}>
              👑 Leadership & Board
            </span>
            <h2 className={`text-[32px] md:text-[42px] font-black ${isLight ? 'text-[#0A2451]' : 'text-white'} leading-tight`}>
              Chapter <span className="text-[#FDBC12]">Leadership & Directors</span>
            </h2>
            <p className={`${isLight ? 'text-slate-600' : 'text-slate-400'} text-sm mt-2 max-w-lg`}>
              Meet the active Rotarians & directors driving growth in RMB Jaipur Fellowship.
            </p>
          </div>
          <button
            className="flex items-center gap-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] px-5 py-2.5 rounded-xl font-black text-xs shadow-lg transition cursor-pointer whitespace-nowrap self-start sm:self-auto"
            onClick={() => {
              window.location.href = '/leadership';
            }}
          >
            View Leadership Team <FaArrowRight />
          </button>
        </div>

        {/* Dynamic Member Cards Grid */}
        {activeMembers.length === 0 ? (
          <div className={`text-slate-400 font-semibold py-12 text-center w-full ${isLight ? 'bg-white border-slate-200' : 'bg-[#0F1D30] border-slate-800'} rounded-3xl border`}>
            No active society members found.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {activeMembers.slice(0, 4).map((data) => (
              <div
                key={data.id}
                className={`group ${isLight ? 'bg-white border-slate-200 shadow-xl text-slate-900' : 'bg-gradient-to-b from-[#0F1D30] to-[#0A1527] border-slate-800 text-white'} border hover:border-[#FDBC12]/60 rounded-3xl p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl relative overflow-hidden`}
              >
                {/* Points Pill Badge */}
                <div className="absolute top-4 right-4 bg-[#FDBC12]/10 border border-[#FDBC12]/30 text-[#FDBC12] text-[10px] font-black px-2.5 py-0.5 rounded-full flex items-center gap-1">
                  <FaAward className="text-xs" /> {data.point || 0} pts
                </div>

                {/* Profile Image */}
                <div className="relative w-24 h-24 rounded-full p-1 bg-gradient-to-tr from-[#FDBC12] to-amber-500/20 shadow-xl mb-4 mt-2 group-hover:scale-105 transition-transform duration-300">
                  <img
                    className="w-full h-full rounded-full object-cover bg-slate-900"
                    src={
                      data.profile
                        ? (data.profile.startsWith('http')
                            ? data.profile
                            : `https://rmbbackend.rmbjaipur.com/${data.profile.replace(/\\/g, '/')}`)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name)}&background=0A2451&color=FDBC12`
                    }
                    alt={data.name}
                    onError={(e) => {
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name)}&background=0A2451&color=FDBC12`;
                    }}
                  />
                </div>

                {/* Name & Role */}
                <h3 className="text-lg font-black text-white group-hover:text-[#FDBC12] transition-colors line-clamp-1">
                  {data.name}
                </h3>
                <p className="text-[#FDBC12] text-xs font-bold mt-1 line-clamp-1">
                  {data.designation || 'RMB Member'}
                </p>
                {data.company_name && (
                  <p className="text-slate-400 text-xs font-medium mt-0.5 line-clamp-1">
                    {data.company_name}
                  </p>
                )}

                {/* About Bio */}
                <p className="text-slate-500 text-xs mt-3 line-clamp-2 leading-relaxed flex-1">
                  {data.about_company || 'Active Rotarian member in RMB Jaipur fellowship chapter.'}
                </p>

                {/* Bottom Action Footer */}
                <div className="w-full pt-4 mt-4 border-t border-slate-800/80 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-500">
                    <span className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-[#FDBC12] hover:text-[#0A2451] flex items-center justify-center text-xs transition cursor-pointer">
                      <FaLinkedinIn />
                    </span>
                    <span className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-[#FDBC12] hover:text-[#0A2451] flex items-center justify-center text-xs transition cursor-pointer">
                      <FaFacebookF />
                    </span>
                    <span className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-[#FDBC12] hover:text-[#0A2451] flex items-center justify-center text-xs transition cursor-pointer">
                      <FaInstagram />
                    </span>
                  </div>

                  <button
                    className="bg-[#FDBC12]/10 hover:bg-[#FDBC12] text-[#FDBC12] hover:text-[#0A2451] border border-[#FDBC12]/30 px-3 py-1.5 rounded-xl text-xs font-black transition cursor-pointer"
                    onClick={() => {
                      window.location.href = '/members';
                    }}
                  >
                    Connect
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Teams;
